import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ShieldCheck, FolderOpen } from 'lucide-react';
import { ApprovalActions, ApprovalBadge } from '../components/rd/ApprovalControls';
import { LineDiff } from '../components/rd/LineDiff';

const TABS = [['pending', 'Pending'], ['rejected', 'Rejected'], ['approved', 'Approved'], ['all', 'All']];
const fmt = (s) => (s ? new Date(s).toLocaleString() : '—');

const ApprovalRow = ({ d, onChange }) => {
  const navigate = useNavigate();
  const a = d.approval || {};
  const target = d.kind === 'rework'
    ? `Next revision of ${d.bom_variant_code} (replaces rev ${d.source_bom_revision})`
    : `New BOM · variant ${a.promote_payload?.bom_variant_code || '—'}`;
  return (
    <Card className="p-4 space-y-2" data-testid={`approval-row-${d.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800">{d.name}</span>
            <Badge variant="outline" className="text-[10px] font-mono">{d.revision}</Badge>
            {d.kind === 'rework' && <Badge className="text-[10px] bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200">Rework</Badge>}
            <ApprovalBadge draft={d} />
            {a.status === 'approved' && <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">Approved{a.bom_revision ? ` → rev ${a.bom_revision}` : ''}</Badge>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{target} · {(d.lines || []).length} lines</p>
          <p className="text-xs text-slate-500">Submitted by {a.submitted_by_name || '—'} · {fmt(a.submitted_at)}{a.status !== 'pending' && a.by_name ? ` · decided by ${a.by_name} ${fmt(a.at)}` : ''}</p>
          {a.comment && <p className="text-xs text-slate-700 mt-1 italic">“{a.comment}”</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => navigate(`/rd/${d.project_id}?tab=boms`)} data-testid={`approval-open-project-${d.id}`}>
            <FolderOpen className="w-3.5 h-3.5 mr-1" />{d.project_name || 'Project'}
          </Button>
          <ApprovalActions draft={d} onChange={onChange} />
        </div>
      </div>
      {d.diff_vs_production && (
        <div>
          <p className="text-[11px] font-medium text-slate-600 mb-1">Changes vs production rev {d.source_bom_revision}</p>
          <LineDiff diff={d.diff_vs_production} testId={`approval-diff-${d.id}`} />
        </div>
      )}
    </Card>
  );
};

export default function RDApprovalsPage() {
  const { api } = useAuth();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api().get('/rd/approvals', { params: { status } }).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [api, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-5" data-testid="rd-approvals-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-emerald-600" />BOM Approvals</h1>
        <p className="text-sm text-slate-500 mt-1">Every R&D → production promotion needs an Admin or Production Supervisor to approve it. Approve promotes it automatically; Reject sends it back to R&D.</p>
      </div>
      <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="approvals-status-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => { setRows(null); setStatus(k); }} className={`px-4 py-1.5 text-sm transition-colors ${status === k ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`approvals-tab-${k}`}>{label}</button>
        ))}
      </div>
      {rows === null ? <p className="text-sm text-slate-500">Loading…</p>
        : rows.length === 0 ? <Card className="p-8 text-center text-sm text-slate-500" data-testid="approvals-empty">Nothing {status === 'all' ? 'here' : status} right now.</Card>
        : <div className="space-y-3">{rows.map(d => <ApprovalRow key={d.id} d={d} onChange={load} />)}</div>}
    </div>
  );
}
